import { supabase } from './supabase'

/**
 * Employment records — where somebody sits and what they do (049).
 *
 * Separate from lib/admin.ts for the reason 021 and 022 keep returning to: the
 * acts differ. Suspending a colleague and reading the address their invitation
 * went to are ACCESS acts, gated on `admin.roles`. Setting a job title, a
 * business unit and a reporting line is an ORG act, gated on `admin.org` — a
 * permission that had been declared and enforced by nothing since migration 002
 * until this shipped.
 *
 * ── set_employment IS A REPLACE, NOT A PATCH ────────────────────────────────
 *
 * Every field is sent on every call, and the server takes no defaults. With
 * defaults, "clear the job title" and "leave the job title alone" would be the
 * same request — 046's null-versus-empty problem, except the silent outcome is
 * data loss rather than an unfiltered list. So the caller is a form and the
 * form always posts the whole record. Do not add optional arguments here to
 * save typing at a call site.
 *
 * ── THE PROJECTION IS THE SERVER'S JOB ──────────────────────────────────────
 *
 * `org_unit_membership` is what `people_and_roles` renders and what its
 * cascading org filter searches, and set_employment writes it in the same
 * transaction as the employment row. Nothing in the client may write
 * memberships — the table has no INSERT policy and must not gain one, because
 * the moment there are two writers the admin screen can filter on a placement
 * the record disagrees with, and it will look correct while doing it.
 */

export type EmploymentRecord = {
  legalEntityId: string | null
  legalEntity: string | null
  businessUnitId: string | null
  businessUnit: string | null
  departmentId: string | null
  department: string | null
  jobTitle: string | null
  managerPersonId: string | null
  manager: string | null
  employmentStatus: string
  startedOn: string | null
}

export type EmploymentInput = {
  legalEntityId: string | null
  businessUnitId: string | null
  departmentId: string | null
  jobTitle: string | null
  managerPersonId: string | null
  employmentStatus: string
  startedOn: string | null
}

/**
 * Write the record. Returns the employment id.
 *
 * The server refuses a business unit that does not sit under the legal entity
 * beside it, a department belonging to another entity, a person set to manage
 * themselves, and a reporting line that loops. Those messages are written to be
 * read by a person and name what is wrong, so they are shown as-is.
 */
export async function setEmployment(
  personId: string,
  input: EmploymentInput,
): Promise<string> {
  const { data, error } = await supabase.rpc('set_employment', {
    p_person_id: personId,
    p_legal_entity_id: input.legalEntityId,
    p_primary_org_unit_id: input.businessUnitId,
    p_department_org_unit_id: input.departmentId,
    p_job_title: input.jobTitle?.trim() || null,
    p_manager_person_id: input.managerPersonId,
    p_employment_status: input.employmentStatus,
    p_started_on: input.startedOn || null,
  })
  if (error) throw error
  return data as string
}

/**
 * Set or clear somebody's work email (055).
 *
 * ── THIS IS NOT A SIGN-IN ADDRESS AND THE PANEL SAYS SO IN WORDS ────────────
 *
 * `identity` is what somebody signs in with; this is where the company believes
 * it can reach them, copied from BambooHR. It authorises nothing. The two are
 * kept apart deliberately — 010 makes the identity row THE INVITATION, so
 * writing an address here can never accidentally let anybody in.
 *
 * That separation is also why this is a separate RPC and not a field on
 * set_employment. set_employment is a REPLACE, and folding a contact detail into
 * it would mean an admin correcting a job title silently clears an address the
 * roster supplied — the exact failure the replace-versus-patch note above is
 * about, arriving through the one field that is not employment.
 *
 * Gated on `admin.org`, not `admin.roles`: correcting how a colleague is reached
 * is an org act. Changing what they SIGN IN with is an access act and is still
 * ACC-21, unbuilt — the server's refusal message says that, which is why it is
 * shown as-is.
 *
 * An empty string clears the row. That is a real outcome and not a no-op, so the
 * caller has to mean it.
 */
export async function setWorkEmail(
  personId: string,
  value: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_work_email', {
    p_person_id: personId,
    p_value: value?.trim() || null,
  })
  if (error) throw error
}

/**
 * Create a department (or a team).
 *
 * Departments did not exist until 049 — the org_unit type has been permitted
 * since 002 with zero rows — so without this, the department field on the form
 * has nothing to offer. A department takes NO PARENT (049d): the 925-person
 * roster carries fifteen SF Group functional families that span every legal
 * entity, one of them appearing in 35 of 37 divisions, so a department is what
 * somebody DOES and the business unit is where they SIT.
 *
 * The server refuses `legal_entity` and `business_unit` outright. Those came
 * from the 30 Aug export and are checksummed against the asset registry, and
 * hand-creating one is how the org chart stops describing the fleet.
 */
export async function createOrgUnit(
  type: 'department' | 'team',
  name: string,
  parentId: string | null,
): Promise<string> {
  const { data, error } = await supabase.rpc('create_org_unit', {
    p_type: type,
    p_name: name.trim(),
    p_parent_id: parentId,
  })
  if (error) throw error
  return data as string
}
